import { useEffect, useState } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { 
  Mail, Clock, CheckCircle, XCircle, Edit, Send, 
  RefreshCw, User, Package, Calendar, Loader2, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const FOLLOWUP_TYPES = {
  quick_check: { label: 'Quick Check', color: 'bg-blue-500' },
  value_add: { label: 'Value Add', color: 'bg-purple-500' },
  reminder: { label: 'Reminder', color: 'bg-amber-500' },
  final: { label: 'Final', color: 'bg-red-500' }
};

export default function FollowUps() {
  const [user, setUser] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedDraft, setSelectedDraft] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editedMessage, setEditedMessage] = useState('');
  const [isActioning, setIsActioning] = useState(false);

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user'));
    setUser(userData);
    if (userData?.role === 'seller') {
      fetchDueDrafts();
    }
  }, []);

  const fetchDueDrafts = async (showIndicator = false) => {
    if (showIndicator) setIsRefreshing(true);
    try {
      const response = await axios.get(`${API}/followups/due`);
      setDrafts(response.data.drafts || []);
    } catch (error) {
      toast.error('Failed to fetch follow-ups');
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  const handleAction = async (draftId, action, editedMsg = null) => {
    setIsActioning(true);
    try {
      const payload = { action };
      if (editedMsg) payload.edited_message = editedMsg;
      
      await axios.patch(`${API}/followups/${draftId}`, payload);
      
      if (action === 'approve') {
        toast.success('Follow-up approved and queued for sending');
      } else if (action === 'edit') {
        toast.success('Follow-up edited and approved');
      } else if (action === 'skip') {
        toast.info('Follow-up skipped');
      }
      
      setDrafts(drafts.filter(d => d.id !== draftId));
      setShowEditDialog(false);
      setSelectedDraft(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process follow-up');
    } finally {
      setIsActioning(false);
    }
  };

  const openEditDialog = (draft) => {
    setSelectedDraft(draft);
    setEditedMessage(draft.message);
    setShowEditDialog(true);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  if (user?.role !== 'seller') {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-4 py-8 text-center">
          <h1 className="text-2xl font-bold text-primary mb-4">Follow-ups</h1>
          <p className="text-muted-foreground">Follow-ups are available for sellers only.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="followups-page">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="font-heading text-3xl font-bold text-primary mb-2">
              AI Follow-ups
            </h1>
            <p className="text-muted-foreground">
              Review and approve AI-drafted follow-up messages for your leads
            </p>
          </div>
          <Button 
            variant="outline" 
            onClick={() => fetchDueDrafts(true)}
            disabled={isRefreshing}
            data-testid="refresh-followups-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
            Check for Due
          </Button>
        </div>

        {/* Info Card */}
        <Card className="mb-6 bg-indigo-50 border-indigo-200">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-indigo-600 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-indigo-900">How Follow-ups Work</p>
                <p className="text-sm text-indigo-700 mt-1">
                  AI drafts follow-up messages based on your lead's activity. Review each message, 
                  approve as-is, edit before sending, or skip if not needed.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : drafts.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <CheckCircle className="h-12 w-12 mx-auto text-green-500 mb-4" />
              <h3 className="text-lg font-medium mb-2">All Caught Up!</h3>
              <p className="text-muted-foreground">
                No follow-ups are due at the moment. Check back later or add more leads.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {drafts.map((draft) => (
              <Card key={draft.id} data-testid={`followup-draft-${draft.id}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="font-heading text-lg flex items-center gap-2">
                        <Package className="h-4 w-4 text-muted-foreground" />
                        {draft.product_title}
                      </CardTitle>
                      <CardDescription className="flex items-center gap-4 mt-1">
                        <span className="flex items-center gap-1">
                          <User className="h-3 w-3" />
                          {draft.buyer_name}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          Day {draft.sequence_day} follow-up
                        </span>
                      </CardDescription>
                    </div>
                    <Badge 
                      className={FOLLOWUP_TYPES[draft.followup_type]?.color || 'bg-slate-500'}
                    >
                      {FOLLOWUP_TYPES[draft.followup_type]?.label || draft.followup_type}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {/* Subject */}
                    <div>
                      <p className="text-xs font-medium text-muted-foreground mb-1">Subject</p>
                      <p className="text-sm font-medium">{draft.subject}</p>
                    </div>
                    
                    {/* Message Preview */}
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <p className="text-xs font-medium text-muted-foreground mb-2">Message</p>
                      <p className="text-sm whitespace-pre-wrap">{draft.message}</p>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 pt-2">
                      <Button 
                        onClick={() => handleAction(draft.id, 'approve')}
                        disabled={isActioning}
                        className="flex-1"
                        data-testid={`approve-btn-${draft.id}`}
                      >
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Approve & Send
                      </Button>
                      <Button 
                        variant="outline"
                        onClick={() => openEditDialog(draft)}
                        disabled={isActioning}
                        data-testid={`edit-btn-${draft.id}`}
                      >
                        <Edit className="h-4 w-4 mr-2" />
                        Edit
                      </Button>
                      <Button 
                        variant="ghost"
                        onClick={() => handleAction(draft.id, 'skip')}
                        disabled={isActioning}
                        data-testid={`skip-btn-${draft.id}`}
                      >
                        <XCircle className="h-4 w-4 mr-2" />
                        Skip
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Edit Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Edit Follow-up Message</DialogTitle>
              <DialogDescription>
                Customize the message before sending to {selectedDraft?.buyer_name}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <p className="text-sm font-medium mb-2">Subject: {selectedDraft?.subject}</p>
              </div>
              <Textarea
                value={editedMessage}
                onChange={(e) => setEditedMessage(e.target.value)}
                rows={10}
                className="font-mono text-sm"
                data-testid="edit-message-textarea"
              />
              <div className="flex gap-2">
                <Button 
                  onClick={() => handleAction(selectedDraft?.id, 'edit', editedMessage)}
                  disabled={isActioning}
                  className="flex-1"
                  data-testid="save-edit-btn"
                >
                  {isActioning ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving...</>
                  ) : (
                    <><Send className="h-4 w-4 mr-2" /> Save & Send</>
                  )}
                </Button>
                <Button variant="outline" onClick={() => setShowEditDialog(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
